import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getApiUrl } from '../../../config/api';
import { Container } from '@/Components/ui/container';
import { Card, CardContent } from '@/Components/ui/card';
import { Input } from '@/Components/ui/input';
import { Label } from '@/Components/ui/label';
import { Textarea } from '@/Components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/Components/ui/select';
import { Button } from '@/Components/ui/button';
import { Separator } from '@/Components/ui/separator';
import { Stack } from '@/Components/ui/layout';
import { Inline } from '@/Components/ui/layout';
import { Heading } from '@/Components/ui/typography';
import { Text } from '@/Components/ui/typography';
import { Mail, Loader2, X } from 'lucide-react';
import { cn } from '@/lib/utils';

const ContactUs = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const [expandedFaq, setExpandedFaq] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch(getApiUrl('/api/contact'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setSubmitStatus({
          type: 'success',
          message: result.message
        });
        // Reset form after successful submission
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: ''
        });
      } else {
        setSubmitStatus({
          type: 'error',
          message: result.error || 'Failed to send message. Please try again.'
        });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus({
        type: 'error',
        message: 'Network error. Please check your connection and try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleFaq = (index) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  const faqData = [
    {
      question: "How do I post a job?",
      answer: "Navigate to 'Post Job' in your dashboard and fill out the job details form. Our AI will help optimize your posting for better candidate matches."
    },
    {
      question: "How does the AI matching work?",
      answer: "Our AI analyzes job requirements and candidate profiles to find the best matches based on skills, experience, and cultural fit."
    },
    {
      question: "Can I cancel my subscription?",
      answer: "Yes, you can cancel your subscription at any time from your billing dashboard. No long-term contracts required."
    },
    {
      question: "What payment methods do you accept?",
      answer: "We accept all major credit cards, PayPal, and bank transfers for annual plans."
    },
    {
      question: "How long does it take to get a response?",
      answer: "We typically respond to all inquiries within 24 hours during business days."
    },
    {
      question: "Do you offer refunds?",
      answer: "Yes, we offer a 30-day money-back guarantee for all our subscription plans."
    }
  ];

  useEffect(() => {
    const fetchCompanyDetails = async () => {
      try {
        const storedCompanyId = localStorage.getItem('companyAccountId');
        const res = await fetch(getApiUrl(`/api/get_company_profile?id=${encodeURIComponent(storedCompanyId)}`), {
          credentials: 'include', // important for session
        });
        const data = await res.json();

        if (data.success) {
          setFormData(prev => ({
            ...prev,
            name: data.company_name,
            email: data.email
          }));
        }
      } catch (err) {
        console.error('Failed to load company info', err);
      }
    };

    fetchCompanyDetails();
  }, []);


  return (
    <Container size="default" className="max-w-[800px] mx-auto">
      <Stack spacing="lg" className="mb-8 text-center">
        <Heading level="h1" className="text-4xl font-bold text-foreground mb-2">
          Get in Touch
        </Heading>
        <Text size="base" className="text-muted-foreground m-0">
          We're here to help! Reach out to our support team for any questions or assistance.
        </Text>
      </Stack>

      <Card className="relative bg-white rounded-xl p-8 shadow-sm border mb-8">
        <CardContent className="p-0">
          <button
            type="button"
            onClick={() => navigate('/hirewithus/billing')}
            aria-label="Cancel and return to Plans & Billing"
            title="Cancel"
            className="absolute right-4 top-4 inline-flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <X className="h-5 w-5" />
          </button>
          <Inline spacing="md" align="center" className="gap-4 mb-8 pb-6 border-b">
            <div className="text-2xl bg-primary text-white w-[50px] h-[50px] rounded-lg flex items-center justify-center flex-shrink-0">
              <Mail className="w-6 h-6" />
            </div>
            <Stack spacing="xs" className="flex-1">
              <Heading level="h3" className="text-xl font-semibold text-foreground m-0 mb-1">
                Email Support
              </Heading>
              <Text size="base" className="font-semibold text-primary m-0 mb-1">
                support@searchjob.ai
              </Text>
              <Text size="sm" className="text-muted-foreground m-0">
                We typically respond within 24 hours
              </Text>
            </Stack>
          </Inline>

          <Stack spacing="md">
            <Heading level="h3" className="text-xl font-semibold text-foreground mb-6">
              Send us a Message
            </Heading>
            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col">
                  <Label htmlFor="name" className="font-medium text-foreground mb-2 text-sm">
                    Full Name *
                  </Label>
                  <Input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Enter your full name"
                    className="py-3"
                  />
                </div>

                <div className="flex flex-col">
                  <Label htmlFor="email" className="font-medium text-foreground mb-2 text-sm">
                    Email Address *
                  </Label>
                  <Input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="Enter your email address"
                    className="py-3"
                  />
                </div>
              </div>

              <div className="flex flex-col">
                <Label htmlFor="subject" className="font-medium text-foreground mb-2 text-sm">
                  Subject *
                </Label>
                <Select
                  value={formData.subject}
                  onValueChange={(value) => setFormData({ ...formData, subject: value })}
                  required
                >
                  <SelectTrigger id="subject" className="py-3">
                    <SelectValue placeholder="Select a subject" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="technical-issue">Technical Issue</SelectItem>
                    <SelectItem value="billing-question">Billing Question</SelectItem>
                    <SelectItem value="schedule-demo">Schedule a Demo</SelectItem>
                    <SelectItem value="feature-request">Feature Request</SelectItem>
                    <SelectItem value="general-inquiry">General Inquiry</SelectItem>
                    <SelectItem value="partnership">Partnership</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-col">
                <Label htmlFor="message" className="font-medium text-foreground mb-2 text-sm">
                  Message *
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  placeholder="Describe your issue or question in detail..."
                  className="min-h-[100px] resize-y"
                />
              </div>

              {submitStatus && (
                <div className={cn(
                  "py-3 px-4 rounded-lg font-medium text-center mb-4",
                  submitStatus.type === 'success' 
                    ? "bg-[#d1fae5] text-[#065f46] border border-[#a7f3d0]"
                    : "bg-[#fee2e2] text-[#991b1b] border border-[#fca5a5]"
                )}>
                  {submitStatus.message}
                </div>
              )}
              
              <Button 
                type="submit" 
                disabled={isSubmitting}
                className="bg-primary hover:bg-primary/90 text-white py-3.5 px-6 rounded-md font-semibold"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  'Send Message'
                )}
              </Button>
            </form>
          </Stack>
        </CardContent>
      </Card>
    </Container>
  );
};

export default ContactUs;
